import { z } from "zod";

export const emailSchema = z.string().trim().email("Please enter a valid email address").max(254, "Email is too long");

export const phoneSchema = z.string().trim().min(7, "Phone number is too short").max(20, "Phone number is too long").regex(/^[\d\s\-\+\(\)\.]+$/, "Please enter a valid phone number");

export const nameSchema = z.string().trim().min(1, "This field is required").max(100, "Must be under 100 characters");

export const companySchema = z.string().trim().min(1, "Company name is required").max(200, "Must be under 200 characters");

export const messageSchema = z.string().trim().min(1, "Message is required").max(5000, "Message must be under 5000 characters");

export const sourceSchema = z.enum(["general", "sponsor", "stay-in-know", "early-access", "hotel-packages", "ticket-notify", "email-blast"]);

export const generalInquirySchema = z.object({
  firstName: nameSchema,
  lastName: nameSchema,
  email: emailSchema,
  phone: z.string().max(20).optional().or(z.literal("")),
  message: messageSchema,
});

export const creatorRequestSchema = z.object({
  firstName: nameSchema,
  lastName: nameSchema,
  email: emailSchema,
  phone: phoneSchema,
  instagram: z.string().trim().min(1, "Instagram username is required").max(100, "Must be under 100 characters"),
  tickets: z.number().int().min(1, "Choose at least 1 ticket").max(4, "Maximum of 4 tickets"),
  charity: z.boolean().optional(),
  charityTickets: z.number().int().min(0).max(4).optional(),
});

export const creatorApplicationSchema = z.object({
  firstName: nameSchema,
  lastName: nameSchema,
  email: emailSchema,
  phone: phoneSchema,
  instagram: z.string().trim().min(1, "Instagram username is required").max(100, "Must be under 100 characters"),
  dob: z.string().trim().min(1, "Date of birth is required").max(20),
});

export const sponsorSchema = z.object({
  firstName: nameSchema,
  lastName: nameSchema,
  company: companySchema,
  email: emailSchema,
  phone: z.string().max(20).optional().or(z.literal("")),
  message: messageSchema,
});

export const boatSlipSchema = z.object({
  firstName: nameSchema,
  lastName: nameSchema,
  email: emailSchema,
  phone: phoneSchema,
  hasPasses: z.boolean(),
  hasAdmiralPass: z.boolean(),
  partySize: z.number().int().min(1, "Enter at least 1 person").max(100, "That seems like too many — please contact us directly"),
  boatSize: z.string().trim().min(1, "Boat size is required").max(60, "Must be under 60 characters"),
});

export type ValidationError = {
  field: string;
  message: string;
};

export const getValidationErrors = (error: z.ZodError): ValidationError[] => {
  return error.errors.map((err) => ({
    field: err.path.join("."),
    message: err.message,
  }));
};

export const validateEmail = (email: string): string | null => {
  const result = emailSchema.safeParse(email);
  return result.success ? null : result.error.errors[0].message;
};

export const validatePhone = (phone: string): string | null => {
  const result = phoneSchema.safeParse(phone);
  return result.success ? null : result.error.errors[0].message;
};
